import { useQueries } from '@tanstack/react-query';
import {
  fetchIntegrationPropertySettings,
  getIntegrationPropertySettingsQueryKey,
} from 'hooks/useFetchIntegrationPropertySettings';
import { IntegrationStatus } from 'models/InternalIntegrationSetting';
import { typedKeys } from 'utils/funcs';
import { DYNAMIC_PRICING_TOOLS } from './PricingUpdates.constants';

/**
 * useFetchIntegrationPropertySettings exposes property-level status per `enumId` only, so
 * checking which dynamic pricing tool is active for a property requires one
 * request per tool. This hook fans those out in parallel and returns the
 * enabled tool `enumId`s so the UI can select/order by them.
 */
const useFetchPropertyDynamicPricingTools = (
  propertyUid?: string,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const pricingEnumIds = typedKeys(DYNAMIC_PRICING_TOOLS);
  const isEnabled = enabled && !!propertyUid;

  const results = useQueries({
    queries: pricingEnumIds.map((enumId) => {
      const propertyUids = propertyUid ? [propertyUid] : [];

      return {
        queryKey: getIntegrationPropertySettingsQueryKey({
          enumId,
          propertyUids,
        }),
        queryFn: () => fetchIntegrationPropertySettings(enumId, propertyUids),
        enabled: isEnabled,
        staleTime: 30000,
      };
    }),
  });

  const enabledPricingTools = pricingEnumIds.filter((_, index) =>
    results[index].data?.propertySettings?.some(
      (setting) =>
        setting.propertyUid === propertyUid &&
        setting.status === IntegrationStatus.ACTIVE,
    ),
  );

  const isLoading = isEnabled && results.some((result) => result.isLoading);

  return { enabledPricingTools, isLoading };
};

export default useFetchPropertyDynamicPricingTools;
